import type { FullOrderRequest } from '@src/providers/orders.types';
import { isEqual, isPlainObject } from 'lodash-es';
import type { PreTradeScenarioNewOrderSingle } from '../../redux/preTradeApiTypes';

type DiffValue = string | number | boolean | null | undefined | DiffValue[] | { [key: string]: DiffValue };

export type NormalizedOrderData = { [key: string]: DiffValue };

export interface NormalizedOrderDataDiff {
  field: string;
  formValue: DiffValue;
  nosValue: DiffValue;
}

/**
 * Valid top-level key (or dot-notation subfield path) from either side of the comparison:
 * the request we send (FullOrderRequest) or the scenario NOS returned by the backend (PreTradeScenarioNewOrderSingle).
 * A small number of backend-echoed fields (e.g. Aggregations, OrdStatus) are not modelled in either
 * type and fall through to the `string` fallback.
 */
type ExemptFieldPath =
  | keyof FullOrderRequest
  | keyof PreTradeScenarioNewOrderSingle
  | `${keyof FullOrderRequest & string}.${string}`
  | `${keyof PreTradeScenarioNewOrderSingle & string}.${string}`;

/**
 * Fields intentionally excluded from comparison.
 * These are generated/runtime metadata and do not represent editable user intent.
 */
export const EXEMPT_FIELD_PATHS = [
  // Generated when building a new request from form state.
  'ClOrdID',
  'TransactTime',
  // Derived from market data so can vary between calls
  'ExpectedFillPrice',
  'ExpectedFillQty',
  // Forced request parameter that is not user-editable in the pre-trade flow.
  'Parameters.ErrorAction',
  // Backend metadata / runtime lifecycle fields.
  'Timestamp',
  'MessageID',
  'User',
  'SubmitTime',
  'RequestSource',
  'Aggregations',
  'OrderID',
  'DecisionStatus',
  'OrdStatus',
  'CumQty',
  'Revision',
  'subAccounts',
  'submitTimeForRelatedEntitySub',
  // User annotation, not order-affecting.
  'Comments',
  // Non-order-affecting user selections (UI-6854 / UI-6943).
  // SubAccount / Allocation changes that also change Markets are still caught
  // because Markets is compared separately and is not exempt.
  'Group',
  'SubAccount',
  'Allocation',
  // Pre-trade tracking fields injected at submission time (UI-6943).
  // Not present on the scenario NOS so would always show as a diff.
  'PreTradeSessionID',
  'PreTradeRequestID',
] as const satisfies ReadonlyArray<ExemptFieldPath | string>;

const EXEMPT_FIELDS = new Set<string>(EXEMPT_FIELD_PATHS);

/**
 * Performs an exhaustive deep diff across two normalized payloads.
 * Any non-exempt field present in either payload is compared.
 */
export function getNormalizedOrderDataDiff(a: NormalizedOrderData, b: NormalizedOrderData): NormalizedOrderDataDiff[] {
  const diff: NormalizedOrderDataDiff[] = [];
  collectDiff(a, b, '', diff);
  return diff;
}

/**
 * Recursively walks two payload trees and appends leaf-level mismatches into `diff`.
 *
 * `path` tracks the current field location using dot notation
 * (for example `Parameters.Duration`) so callers can identify exactly where
 * a mismatch happened and so `EXEMPT_FIELDS` can skip specific paths.
 */
function collectDiff(formValue: DiffValue, nosValue: DiffValue, path: string, diff: NormalizedOrderDataDiff[]): void {
  // Skip fields that are intentionally excluded from comparison.
  if (path !== '' && EXEMPT_FIELDS.has(path)) {
    return;
  }

  // Recurse only when both sides are plain objects.
  if (isPlainObject(formValue) && isPlainObject(nosValue)) {
    const formRecord = formValue as NormalizedOrderData;
    const nosRecord = nosValue as NormalizedOrderData;

    // Sort keys so traversal (and resulting diff ordering) is deterministic.
    const keys = [...new Set([...Object.keys(formRecord), ...Object.keys(nosRecord)])].toSorted();
    for (const key of keys) {
      // Build nested path strings like `Parameters.Duration`.
      const nextPath = path ? `${path}.${key}` : key;
      collectDiff(formRecord[key], nosRecord[key], nextPath, diff);
    }
    return;
  }

  if (!isEqual(formValue, nosValue)) {
    diff.push({
      // $root is fallback for top-level non-object diffs where no nested key path was built.
      field: path || '$root',
      formValue,
      nosValue,
    });
  }
}
